'use client';
import { FlipDot } from './FlipDot';

// The matchup status, rendered as an 8-column flip-dot display.
// Always exactly 8 character cells so every row lines up:
//   PREGAME  → "PREGAME" + 1 space (yellow) — week open, no scores yet
//   ACTIVE   → "ACTIVE" + 2 spaces (green)  — points on the board, week open
//   FINAL    → "FINAL"  + 3 spaces (white)  — scoring week has closed
// (Blank cells still render as a brick of unlit dots, which is what
// keeps the column widths identical row to row.)
//
// reveal modes:
//   'auto' (default): ACTIVE does the Family Feud wipe — a solid green
//     bar fills left to right, holds a beat, then sweeps to reveal the
//     word, so the row that's live right now is the one that draws the
//     eye. PROJECTD/FINAL keep the quiet scatter.
//   'wipe' / 'scatter': force one mode.
// revealDelay (ms) offsets a wipe's start so rows can cascade.
const STATUS = {
  project: { text: 'PREGAME', theme: 'yellow' },
  active:  { text: 'ACTIVE',   theme: 'green' },
  final:   { text: 'FINAL',    theme: 'white' },
};

// Height matching (see `matchHeight`): a SplitFlap row is
//   1.06*size (cell) + 20 (its 10px top/bottom padding)
// A FlipDot brick is 7 dot rows: 7*dot + 6*gap, plus its own vertical padding.
// dotSize must stay a whole number or the dots blur, and no integer dot size
// lands exactly on the split-flap height -- so we round to the nearest dot and
// absorb the leftover (up to ~4px) in the panel's vertical padding. Result is
// an exact match at every size instead of a block that drifts as the board
// scales. Pass `matchSize` (the row's SplitFlap size) to enable this.
const SF_PAD = 10;      // .sf-row padding, top and bottom
const SF_CELL = 1.06;   // cell height as a multiple of font-size
const DOT_ROWS = 7;

// Ceiling on the dot. WHY THIS EXISTS:
//
// The status block is 8 character cells wide and its width is driven
// entirely by dotSize -- roughly 49px of row width per 1px of dot. Left
// uncapped it scaled to dot=7 on a TV, making the block 384px: the single
// widest element on the row, wider than either team block. That is what
// pushed the right edge off-screen in fullscreen.
//
// It also doesn't need to be that big. The status is three fixed words
// read at a glance from across a room; legibility saturates long before
// the dots get chunky. 5 holds the block at 286px -- comfortably narrower
// than a team block at every size -- and is still crisp on a 4K panel.
//
// Height still matches the row exactly: `padY` absorbs the difference, so
// a capped block centers itself in the row instead of growing to fill it.
const DOT_MAX = 5;

export function statusMetrics(matchSize, gap = 1) {
  const target = SF_CELL * matchSize + SF_PAD * 2;
  const raw = Math.round((target - (DOT_ROWS + 1) * gap) / (DOT_ROWS + 2));
  const dotSize = Math.min(DOT_MAX, Math.max(2, raw));
  const padY = Math.max(0, (target - (DOT_ROWS * dotSize + (DOT_ROWS - 1) * gap)) / 2);
  return { dotSize, gap, padY, target };
}

// Rendered width of the status block in px, for a given row size.
//
// The layout budget needs this as a real measurement because the block does
// NOT scale linearly with `size` -- it steps in discrete jumps and is
// proportionally much wider on small rows. Assuming otherwise is what made
// the old flat `size * 44` row estimate wrong at the bottom of the range.
// See useBoardScale.
export function statusWidth(matchSize, gap = 1) {
  const { dotSize } = statusMetrics(matchSize, gap);
  const brick = 5 * dotSize + 4 * gap;   // 5 dot columns per character
  const brickGap = dotSize + gap;
  const padX = dotSize + gap;
  return 8 * brick + 7 * brickGap + 2 * padX;
}

export default function StatusFlap({
  status, dotSize = 3, matchSize = null, speed = 1, reveal = 'auto', revealDelay = 0,
}) {
  const s = STATUS[status] || STATUS.final;
  const mode = reveal === 'auto' ? (status === 'active' ? 'wipe' : 'scatter') : reveal;
  const m = matchSize ? statusMetrics(matchSize) : null;
  return (
    <FlipDot
      text={s.text.padEnd(8, ' ')}
      theme={s.theme}
      dotSize={m ? m.dotSize : dotSize}
      gap={1}
      speed={speed}
      reveal={mode}
      revealDelay={revealDelay}
      aria-label={s.text}
      style={m ? { padding: `${m.padY}px ${m.dotSize + 1}px`, boxSizing: 'border-box' } : undefined}
    />
  );
}
